import { useState } from "react";
import { ExternalLinkIcon } from "@heroicons/react/outline";

const formatLocation = (address) => {
  if (!address) return "-";
  const parts = [address.city, address.country];
  const filtered = parts.filter((p) => p && p.trim() !== "");
  return filtered.length ? filtered.join(", ") : "-";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

// ── Status badge ──
const statusBadge = (value) => {
  if (!value) return <span className="text-slate-500">—</span>;
  const v = value.toLowerCase();
  const cls =
    v.includes("paid") || v.includes("confirm")
      ? "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
      : v.includes("cancel")
      ? "bg-red-500/10 text-red-400 border-red-500/20"
      : v.includes("pending") || v.includes("await")
      ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
      : "bg-slate-500/10 text-slate-400 border-slate-500/20";
  return (
    <span className={`inline-block px-2.5 py-0.5 rounded-md text-xs font-medium border capitalize ${cls}`}>
      {value}
    </span>
  );
};

// ── Info row ──
const Info = ({ label, value, badge }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b border-white/5 last:border-none">
    <span className="text-xs text-slate-500 shrink-0 w-32">{label}</span>
    {badge ? statusBadge(value) : <span className="text-sm text-slate-200 text-right">{value || "—"}</span>}
  </div>
);

const MainInfoTab = ({ booking }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

      {/* ── LEFT — Booking details + Important links ── */}
      <div className="space-y-5">
        <div className="bg-slate-900/60 rounded-2xl p-5 space-y-1">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Booking Details</h3>

          <Info label="Promoter"        value={booking.promoter?.name} />
          <Info label="Event Date"      value={booking.date} />
          <Info label="Location"        value={formatLocation(booking.address)} />
          <Info label="Set Time"        value={booking.set_time} />
          <Info label="Artist"          value={booking.artist_name} />
          <Info label="Payment Status"  value={booking.payment_status}  badge />
          <Info label="Booking Status"  value={booking.status}          badge />

          {/* Event Flyer */}
          <div className="pt-4 mt-2">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-medium mb-2">Event Flyer</p>
            <div className="bg-slate-800/60 rounded-xl h-44 flex items-center justify-center overflow-hidden border border-white/5">
              {booking.event_flyer ? (
                <img src={booking.flyer} alt="Event Flyer" className="h-full w-full object-cover" />
              ) : (
                <span className="text-slate-600 text-xs">No flyer uploaded</span>
              )}
            </div>
          </div>
        </div>

        {/* Important Links */}
        <div className="bg-slate-900/60 rounded-2xl p-5">
          <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Important Links</h3>

          {booking.links?.length ? (
            <div className="space-y-2">
              {booking.links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-between gap-3 px-4 py-3 bg-slate-800/50 hover:bg-slate-800 border border-white/5 rounded-xl transition group"
                >
                  <span className="text-sm text-slate-300 group-hover:text-white truncate">{link.label}</span>
                  <ExternalLinkIcon className="w-3.5 h-3.5 text-slate-500 group-hover:text-pink-400 shrink-0 transition" />
                </a>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center py-8 text-slate-600 text-sm">
              No links available
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT — Activity logs ── */}
      <div className="bg-slate-900/60 rounded-2xl p-5 flex flex-col">
        <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Activity Logs</h3>

        <div className="flex-1 overflow-y-auto space-y-3 max-h-96 pr-1"
          style={{ scrollbarWidth: "thin", scrollbarColor: "rgba(255,255,255,0.08) transparent" }}
        >
          {booking.activity_logs?.length ? (
            booking.activity_logs.map((log) => (
              <div key={log.id} className="relative pl-4 pb-3 border-b border-white/5 last:border-none">
                {/* Timeline dot */}
                <span className="absolute left-0 top-1.5 w-1.5 h-1.5 rounded-full bg-pink-500/60" />
                <div className="flex items-start justify-between gap-2">
                  <span className="text-xs font-medium text-white leading-tight">{log.action}</span>
                  <span className="text-[10px] text-slate-600 shrink-0 mt-0.5">{formatDate(log.date)}</span>
                </div>
                {log.description && (
                  <p className="text-xs text-slate-500 mt-1 leading-relaxed">{log.description}</p>
                )}
              </div>
            ))
          ) : (
            <div className="flex items-center justify-center py-8 text-slate-600 text-sm">
              No activity yet
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default MainInfoTab;